import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import apiClient from "../common/http.service";

export const useCampaigns = (page = 1, limit = 10, search = "") => {
    const queryClient = useQueryClient();

    const campaignQuery = useQuery({
        queryKey: ["campaigns", page, limit, search],
        queryFn: async () => {
            const params = {
                page,
                pageSize: limit,
            };

            if (search) params.search = search;

            const queryString = new URLSearchParams(params).toString();
            const response = await apiClient.get(`/campaign?${queryString}`);
            const payload = response?.data?.data || {};

            return {
                campaigns: Array.isArray(payload.data) ? payload.data : [],
                pagination: payload.pagination || {
                    totalRecords: 0,
                    totalPages: 1,
                },
            };
        },

        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        keepPreviousData: true,
    });

    const createMutation = useMutation({
        mutationFn: (data) => apiClient.post("/campaign", data),
        onSuccess: () => {
            toast.success("Campaign created successfully");

            queryClient.invalidateQueries({
                queryKey: ["campaigns"],
            });
        },
        onError: (err) =>
            toast.error(err.response?.data?.message || "Create failed"),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) =>
            apiClient.put(`/campaign/${id}`, data),
        onSuccess: () => {
            toast.success("Campaign updated successfully");

            queryClient.invalidateQueries({
                queryKey: ["campaigns"],
            });
        },
        onError: (err) =>
            toast.error(err.response?.data?.message || "Update failed"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => apiClient.delete(`/campaign/${id}`),
        onSuccess: () => {
            toast.success("Campaign deleted successfully");

            queryClient.invalidateQueries({
                queryKey: ["campaigns"],
            });
        },
        onError: (err) =>
            toast.error(err.response?.data?.message || "Delete failed"),
    });

    return {
        campaigns: campaignQuery.data?.campaigns || [],
        pagination: campaignQuery.data?.pagination || {},

        isLoading: campaignQuery.isLoading,
        isError: campaignQuery.isError,
        error: campaignQuery.error,

        createCampaign: createMutation.mutateAsync,
        isCreating: createMutation.isPending,

        updateCampaign: updateMutation.mutateAsync,
        isUpdating: updateMutation.isPending,

        deleteCampaign: deleteMutation.mutateAsync,
        isDeleting: deleteMutation.isPending,
    };
};
